// Journal articles with full content. Used by JournalDetail.jsx.

export const journalArticles = [
  {
    id: 'j1', slug: 'designing-the-unfamiliar',
    title: 'On designing the unfamiliar',
    date: 'Mar 12, 2025', read: '6 min', cat: 'AI UX',
    cover: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1600&q=85',
    excerpt: 'Designing for AI is mostly designing for ambiguity. Notes from shipping three agentic products this year.',
    tags: ['AI', 'UX patterns', 'Generative interfaces'],
    body: [
      { type: 'p', text: 'Most of what we do as designers is reduce uncertainty. We turn unknown processes into clear, predictable interfaces. Then we ship them and move on. AI breaks that contract.' },
      { type: 'p', text: 'A generative system can produce a hundred plausible responses to the same input. Users feel that ambiguity, even if they cannot name it. They distrust it. They click away.' },
      { type: 'h2', text: 'Three patterns that helped' },
      { type: 'p', text: 'Across three shipped products this year, the same three patterns kept earning their place in the design system.' },
      { type: 'h3', text: '1. Show the seam' },
      { type: 'p', text: 'When the model is thinking, say so. When it is uncertain, show it. The seams of an AI feature are not bugs to hide \u2014 they are trust signals to design.' },
      { type: 'h3', text: '2. Make the rerun obvious' },
      { type: 'p', text: 'The single biggest UX upgrade I have made this year is a clear, never-hidden rerun button next to every generated artifact. Lower the cost of disagreement and users will explore.' },
      { type: 'h3', text: '3. Always give a way out' },
      { type: 'p', text: 'An AI feature without a manual escape is a UX trap. Every generative surface should ship with a "do it myself" affordance, no matter how small.' },
      { type: 'quote', text: 'AI features feel magical until they feel manipulative. The line is shorter than you think.' },
      { type: 'p', text: 'Three months in, the products that ship the boring patterns above outperform the ones that try to wow. Predictability is the new differentiator.' },
    ],
  },
  {
    id: 'j2', slug: 'design-system-that-scales',
    title: 'Building a design system that scales',
    date: 'Feb 18, 2025', read: '5 min', cat: 'DesignOps',
    cover: 'https://images.unsplash.com/photo-1481487196290-c152efe083f5?w=1600&q=85',
    excerpt: 'Notes from running a token-first design system across multiple product tracks.',
    tags: ['Design systems', 'DesignOps', 'Tokens'],
    body: [
      { type: 'p', text: 'Token-first sounds obvious now. It was not obvious when we started. The decision that made our system scale was committing to tokens as the source of truth, with components consuming them \u2014 never the other way around.' },
      { type: 'h2', text: 'What we got right' },
      { type: 'p', text: 'Three early decisions paid off in compounding ways.' },
      { type: 'h3', text: '1. A small, opinionated palette' },
      { type: 'p', text: 'We capped the system at 11 colour roles, not 80 swatches. Designers complained for a month. Then nobody complained again.' },
      { type: 'h3', text: '2. Density modes, not separate themes' },
      { type: 'p', text: 'Comfortable and compact density modes were a single token override away. We avoided forking the system for "table-heavy" products.' },
      { type: 'h3', text: '3. Engineering owns the runtime' },
      { type: 'p', text: 'Designers ship Figma. Engineers ship code. The token pipeline is engineering-owned and reviewed like product code. No mystery JSON files.' },
      { type: 'h2', text: 'What we got wrong' },
      { type: 'p', text: 'We under-invested in documentation in year one. The system worked because the team was small. It nearly broke when the team doubled. Doc is product work, not an afterthought.' },
    ],
  },
  {
    id: 'j3', slug: 'accessibility-end-to-end',
    title: 'Designing for accessibility, end-to-end',
    date: 'Jan 14, 2025', read: '7 min', cat: 'Craft',
    cover: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1600&q=85',
    excerpt: 'Accessibility is a workflow, not a checklist. A practical look at how we built it into our weekly cadence.',
    tags: ['Accessibility', 'Inclusive design', 'Process'],
    body: [
      { type: 'p', text: 'A11y audits at the end of a release are too late. They become a triage list. By the time the team gets to "yellow" issues, the next sprint has started.' },
      { type: 'h2', text: 'The weekly cadence that worked' },
      { type: 'h3', text: 'Monday \u2014 contrast pass' },
      { type: 'p', text: 'Every new screen runs through an automated contrast checker before it leaves the designer\u2019s hands.' },
      { type: 'h3', text: 'Wednesday \u2014 keyboard pass' },
      { type: 'p', text: 'A 20-minute pairing session between a designer and an engineer. Tab through everything. Note everything that breaks.' },
      { type: 'h3', text: 'Friday \u2014 reader pass' },
      { type: 'p', text: 'Run the screen through a screen reader once a week. Most of the issues are not labelling \u2014 they are focus order.' },
      { type: 'p', text: 'A11y stops being a project when it becomes a habit. The cadence above costs roughly two hours a week and pays back in tickets-not-filed.' },
    ],
  },
  {
    id: 'j4', slug: 'microinteractions-macro-impact',
    title: 'Microinteractions, macro impact',
    date: 'Dec 02, 2024', read: '5 min', cat: 'Motion',
    cover: 'https://images.unsplash.com/photo-1581090700227-1e37b190418e?w=1600&q=85',
    excerpt: 'The little things make products feel alive. A measured ode to motion done with restraint.',
    tags: ['Motion', 'Interaction', 'Craft'],
    body: [
      { type: 'p', text: 'Motion is the easiest place to make a product feel premium. It is also the easiest place to make it feel cheap.' },
      { type: 'h2', text: 'Two rules I keep coming back to' },
      { type: 'h3', text: '1. Motion has a job' },
      { type: 'p', text: 'Every animation should answer a question: where did this come from, where did it go, what just changed. If it cannot, cut it.' },
      { type: 'h3', text: '2. Easing is everything' },
      { type: 'p', text: 'A 200ms ease-out feels like care. A 200ms linear transition feels like a machine. Same duration, completely different products.' },
    ],
  },
  {
    id: 'j5', slug: 'design-leadership-without-the-title',
    title: 'Design leadership, without the title',
    date: 'Nov 04, 2024', read: '6 min', cat: 'Leadership',
    cover: 'https://images.unsplash.com/photo-1552581234-26160f608093?w=1600&q=85',
    excerpt: 'Notes from a decade of leading designers \u2014 critique culture, hiring bars, and the quiet art of raising the room.',
    tags: ['Design leadership', 'Critique', 'Hiring'],
    body: [
      { type: 'p', text: 'You become a design leader long before the title. The job starts the day you stop optimising for your own portfolio and start optimising for the work the team ships.' },
      { type: 'h2', text: 'Three habits that compound' },
      { type: 'h3', text: '1. Write the critique you want to receive' },
      { type: 'p', text: 'Public, written critiques \u2014 not Slack one-liners. Pick the strongest two issues. Acknowledge what is working before you criticise. Make the bar visible.' },
      { type: 'h3', text: '2. Hire for trajectory, not portfolio' },
      { type: 'p', text: 'A portfolio is a snapshot; trajectory is a derivative. The best designers I have hired had ordinary portfolios but had grown the most between projects.' },
      { type: 'h3', text: '3. Spend your seniority on courage, not opinions' },
      { type: 'p', text: 'Use the room you take to ask the awkward business question, defend the junior\u2019s controversial call, and kill the safe but soulless concept. That is what seniority is actually for.' },
    ],
  },
  {
    id: 'j6', slug: 'ai-in-the-design-stack',
    title: 'Where AI actually fits in the design stack',
    date: 'Oct 09, 2024', read: '7 min', cat: 'AI UX',
    cover: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1600&q=85',
    excerpt: 'A pragmatic map of where AI earns its place \u2014 and where it quietly costs you more than it gives.',
    tags: ['AI', 'Workflow', 'Tools'],
    body: [
      { type: 'p', text: 'AI is in every demo. AI is not in every workflow. After a year of integrating it into a 10-person design team, here is where the line actually sits.' },
      { type: 'h2', text: 'Where AI pays back immediately' },
      { type: 'p', text: 'Research synthesis, copy iteration, low-fi exploration, design-to-code handoff, and meeting follow-ups. These are the four places I would not give up an AI assist for any team I run.' },
      { type: 'h2', text: 'Where AI quietly costs you' },
      { type: 'p', text: 'Final visual polish, brand-critical typography, accessibility judgement calls, and any moment that requires a strong opinion about taste. Outsource these and the product starts to feel generic in ways users cannot articulate but will quietly punish you for.' },
      { type: 'quote', text: 'AI is a great intern and a mediocre principal. Treat it accordingly.' },
    ],
  },
];

export const journalBySlug = (slug) => journalArticles.find((j) => j.slug === slug);
